/**
 * CAP install scheduling (PPC): visibility, copy, empty-state messaging, and SOW
 * Install service-appointment selection.
 */
import { orderBy } from "lodash";
import {
  type BookInstallAppointmentResponse,
  SiteSurveyAppointment,
} from "../../../amplify/backend/function/OfferExpCheckout/ts/public/types";
import { type Sow, type SowServiceAppointment } from "../capTypes";
import { type SiteSurveySlot } from "checkout/api/siteSurvey";

type AvailableServiceSlotRaw = {
  appointmentId: string;
  gradeRating: number;
  arrivalEndDate: string;
  arrivalStartDate: string;
  startTime?: string;
  endTime?: string;
  resourceId?: string;
};

const INSTALL_RECORD_TYPE = "Install";

/** FSL available-service-slots max window; a second call uses offset + this. */
const INSTALL_AVAILABLE_SLOTS_WINDOW_DAYS = 30;

/** Fallback start offset when self-scheduler row cannot be loaded. */
const DEFAULT_INSTALL_SCHEDULING_DAYS_OUT = 45;

/** @deprecated Use INSTALL_AVAILABLE_SLOTS_WINDOW_DAYS. Kept for callers/tests. */
const getInstallAvailableSlotsWindowDays = (): number =>
  INSTALL_AVAILABLE_SLOTS_WINDOW_DAYS;

const INSTALL_AVAILABLE_SLOTS_QUERY = {
  isComplexAppointment: "false",
} as const;

type PostCapInstallSchedulerProspect = {
  scheduleAtCAP?: string;
};

const shouldShowPostCapInstallScheduler = (
  prospect?: PostCapInstallSchedulerProspect
): boolean => prospect?.scheduleAtCAP === "Eligible";

const getInstallServiceAppointments = (
  sow: Sow | undefined
): SowServiceAppointment[] =>
  sow?.serviceAppointments?.filter(
    (a) => a.recordType === INSTALL_RECORD_TYPE
  ) ?? [];

const getScheduledInstallServiceAppointment = (
  sow: Sow | undefined
): SowServiceAppointment | undefined =>
  getInstallServiceAppointments(sow).find((a) =>
    ["Scheduled", "Dispatched"].includes(a.status)
  );

const getReadyInstallServiceAppointment = (
  sow: Sow | undefined
): SowServiceAppointment | undefined =>
  getInstallServiceAppointments(sow).find(
    (a) => a.status === "Ready To Be Scheduled"
  );

/**
 * ARC cancels the Install SA; UI must not mention ARC. `serviceAppointments`
 * omits cancelled SAs, so `mostRecentlyCancelledAppointment` carries it.
 */
const isInstallServiceAppointmentCancelled = (sow: Sow | undefined): boolean =>
  !getReadyInstallServiceAppointment(sow) &&
  !getScheduledInstallServiceAppointment(sow) &&
  (!!sow?.mostRecentlyCancelledAppointment ||
    getInstallServiceAppointments(sow).some((a) => a.status === "Cancelled"));

const POST_CAP_INSTALL_CANCELLED_WARNING_MESSAGE =
  "Your install service appointment has been cancelled. Please contact the support team for help at";

const getInstallServiceAppointmentId = (
  sow: Sow | undefined
): string | undefined => {
  const scheduled = getScheduledInstallServiceAppointment(sow);
  const ready = getReadyInstallServiceAppointment(sow);
  return scheduled?.id ?? ready?.id;
};

const ISO_OFFSET_PATTERN = /[Zz]|[+-]\d{2}:\d{2}$/;

/** SOW omits Z but arrival windows match booked UTC slot instants. */
const toUtcIsoFromSowArrivalWindow = (value: string | null): string => {
  if (!value) return "";
  return ISO_OFFSET_PATTERN.test(value) ? value : `${value}Z`;
};

const mapSowInstallToSiteSurveyAppointment = (
  install: SowServiceAppointment
): SiteSurveyAppointment => {
  const status = [
    "Scheduled",
    "Dispatched",
    "Ready To Be Scheduled",
    "Cancelled",
  ].includes(install.status)
    ? (install.status as SiteSurveyAppointment["status"])
    : "Scheduled";

  return {
    address: "",
    appointmentId: install.id,
    appointmentNumber: "",
    appointmentType: "Install",
    arrivalEndDate: toUtcIsoFromSowArrivalWindow(install.arrivalWindowEndTime),
    arrivalStartDate: toUtcIsoFromSowArrivalWindow(
      install.arrivalWindowStartTime
    ),
    cancellable: false,
    cancellationReasons: [],
    customSchedule: false,
    email: "",
    flexibility: false,
    form: false,
    formVersion: 0,
    homeownerName: "",
    opportunityId: "",
    phone: "",
    prospectId: "",
    reschedulable: true,
    rescheduleReasons: ["Other"],
    status,
  };
};

const POST_CAP_INSTALL_SECTION_TITLE = "Schedule your installation";

const POST_CAP_INSTALL_SECTION_DESCRIPTION_BASE =
  "Our installation crew will visit your home to install your Sunrun system. Available dates are calculated based on project characteristics and local permitting timelines.";

const getPostCapInstallSectionDescription = (
  numDaysForInstall?: number
): string => {
  const days = Number(numDaysForInstall) || 1;
  const dayLabel = days === 1 ? "day" : "days";

  return `${POST_CAP_INSTALL_SECTION_DESCRIPTION_BASE}

Your installation is estimated to be completed within ${days} working ${dayLabel}, including the start date.`;
};

const POST_CAP_INSTALL_EMPTY_SLOTS_MESSAGE =
  "No appointments available for selected dates. Try checking later dates. If issue persists contact sales support desk.";

const POST_CAP_INSTALL_LOAD_SLOTS_BUTTON = "Load install appointments";

const POST_CAP_INSTALL_PPC_PREREQUISITE_MESSAGE =
  "You'll need to schedule the post final design home review first.";

const POST_CAP_INSTALL_SCHEDULED_INFO_PILL_LABEL =
  "Customer needs to confirm via text message";

const POST_CAP_INSTALL_CUSTOMER_CONFIRM_MODAL_TITLE =
  "Customer needs to confirm";

const POST_CAP_INSTALL_CUSTOMER_CONFIRM_MODAL_BODY =
  "A text message will be sent to the customer to confirm the appointment time. Please have the customer confirm the appointment by replying to the text message. Appointment time will be cancelled if not confirmed within 60 minutes.";

const POST_CAP_INSTALL_SCHEDULED_SUCCESS_PILL_LABEL = "Customer has confirmed";

const INSTALL_APPOINTMENT_CONFIRMATION_METHOD_CUSTOMER_CONFIRMED =
  "Customer Confirmed";

const POST_CAP_INSTALL_SCHEDULED_CARD_MAX_WIDTH = "472px";

const POST_CAP_INSTALL_SCHEDULED_ARRIVAL_INTRO_START_ONLY =
  "You have scheduled your installation. Our installation crew will arrive at:";

const INSTALL_APPOINTMENT_CONFIRMATION_FAST_POLL_INTERVAL_MS = 10_000;
const INSTALL_APPOINTMENT_CONFIRMATION_FAST_POLL_DURATION_MS = 60_000;
const INSTALL_APPOINTMENT_CONFIRMATION_SLOW_POLL_INTERVAL_MS = 30_000;
const INSTALL_APPOINTMENT_CONFIRMATION_POLL_DURATION_MS = 5 * 60_000;

const getInstallAppointmentConfirmationPollInterval = (
  elapsedMs: number
): number => {
  if (elapsedMs >= INSTALL_APPOINTMENT_CONFIRMATION_POLL_DURATION_MS) {
    return 0;
  }

  if (elapsedMs < INSTALL_APPOINTMENT_CONFIRMATION_FAST_POLL_DURATION_MS) {
    return INSTALL_APPOINTMENT_CONFIRMATION_FAST_POLL_INTERVAL_MS;
  }

  return INSTALL_APPOINTMENT_CONFIRMATION_SLOW_POLL_INTERVAL_MS;
};

const isInstallAppointmentCustomerConfirmed = (
  appointment: { confirmationMethod?: string } | undefined
): boolean =>
  appointment?.confirmationMethod?.toLowerCase() ===
  INSTALL_APPOINTMENT_CONFIRMATION_METHOD_CUSTOMER_CONFIRMED.toLowerCase();

type InstallScheduledStatusPill = {
  type: "info" | "success";
  text: string;
};

const getInstallScheduledStatusPill = (
  isCustomerConfirmed: boolean
): InstallScheduledStatusPill => ({
  type: isCustomerConfirmed ? "success" : "info",
  text: isCustomerConfirmed
    ? POST_CAP_INSTALL_SCHEDULED_SUCCESS_PILL_LABEL
    : POST_CAP_INSTALL_SCHEDULED_INFO_PILL_LABEL,
});

const shouldFetchInstallSchedulingData = (
  isPpcSchedulingNecessary: boolean
): boolean => !isPpcSchedulingNecessary;

const isBookInstallSuccessful = (
  result: BookInstallAppointmentResponse
): boolean => result.salesforceSlotBooked === true;

const buildScheduledInstallAppointmentFromBookedSlot = (
  slot: SiteSurveySlot,
  serviceAppointmentId: string
): SiteSurveyAppointment => ({
  address: "",
  appointmentId: serviceAppointmentId,
  appointmentNumber: "",
  appointmentType: "Install",
  arrivalStartDate: slot.arrivalStartDate,
  arrivalEndDate: slot.arrivalEndDate,
  cancellable: false,
  cancellationReasons: [],
  customSchedule: false,
  email: "",
  flexibility: false,
  form: false,
  formVersion: 0,
  homeownerName: "",
  opportunityId: "",
  phone: "",
  prospectId: "",
  reschedulable: true,
  rescheduleReasons: ["Other"],
  status: "Scheduled",
});

/**
 * FSL available-service-slots may omit startTime/endTime and return multiple
 * resource rows per day; normalize for SiteSurveySlotPicker (one slot per day).
 */
const normalizeAvailableServiceSlots = (
  slots: AvailableServiceSlotRaw[]
): SiteSurveySlot[] => {
  const mapped = slots.map((slot) => ({
    appointmentId: slot.appointmentId,
    gradeRating: slot.gradeRating,
    arrivalStartDate: slot.arrivalStartDate,
    arrivalEndDate: slot.arrivalEndDate,
    startTime: slot.startTime ?? slot.arrivalStartDate,
    endTime: slot.endTime ?? slot.arrivalEndDate,
  }));

  const bestByArrivalStart = new Map<string, SiteSurveySlot>();
  for (const slot of mapped) {
    const existing = bestByArrivalStart.get(slot.arrivalStartDate);
    if (!existing || slot.gradeRating > existing.gradeRating) {
      bestByArrivalStart.set(slot.arrivalStartDate, slot);
    }
  }

  return orderBy(Array.from(bestByArrivalStart.values()), "startTime", "asc");
};

export {
  shouldShowPostCapInstallScheduler,
  getInstallServiceAppointments,
  getScheduledInstallServiceAppointment,
  getReadyInstallServiceAppointment,
  isInstallServiceAppointmentCancelled,
  POST_CAP_INSTALL_CANCELLED_WARNING_MESSAGE,
  getInstallServiceAppointmentId,
  mapSowInstallToSiteSurveyAppointment,
  INSTALL_AVAILABLE_SLOTS_WINDOW_DAYS,
  DEFAULT_INSTALL_SCHEDULING_DAYS_OUT,
  getInstallAvailableSlotsWindowDays,
  INSTALL_AVAILABLE_SLOTS_QUERY,
  POST_CAP_INSTALL_SECTION_TITLE,
  getPostCapInstallSectionDescription,
  POST_CAP_INSTALL_EMPTY_SLOTS_MESSAGE,
  POST_CAP_INSTALL_LOAD_SLOTS_BUTTON,
  POST_CAP_INSTALL_PPC_PREREQUISITE_MESSAGE,
  POST_CAP_INSTALL_SCHEDULED_INFO_PILL_LABEL,
  POST_CAP_INSTALL_CUSTOMER_CONFIRM_MODAL_TITLE,
  POST_CAP_INSTALL_CUSTOMER_CONFIRM_MODAL_BODY,
  POST_CAP_INSTALL_SCHEDULED_SUCCESS_PILL_LABEL,
  INSTALL_APPOINTMENT_CONFIRMATION_METHOD_CUSTOMER_CONFIRMED,
  isInstallAppointmentCustomerConfirmed,
  getInstallScheduledStatusPill,
  getInstallAppointmentConfirmationPollInterval,
  INSTALL_APPOINTMENT_CONFIRMATION_POLL_DURATION_MS,
  INSTALL_APPOINTMENT_CONFIRMATION_FAST_POLL_INTERVAL_MS,
  INSTALL_APPOINTMENT_CONFIRMATION_FAST_POLL_DURATION_MS,
  INSTALL_APPOINTMENT_CONFIRMATION_SLOW_POLL_INTERVAL_MS,
  POST_CAP_INSTALL_SCHEDULED_CARD_MAX_WIDTH,
  POST_CAP_INSTALL_SCHEDULED_ARRIVAL_INTRO_START_ONLY,
  shouldFetchInstallSchedulingData,
  buildScheduledInstallAppointmentFromBookedSlot,
  normalizeAvailableServiceSlots,
  isBookInstallSuccessful,
};
export type {
  AvailableServiceSlotRaw,
  PostCapInstallSchedulerProspect,
  InstallScheduledStatusPill,
};
